import React, { createContext, forwardRef, useContext, useId, useRef, useState } from 'react';
import { cn } from '../../../lib/cn';
import { Icon } from '../../Icon';

/**
 * Tabs, the underline tab set.
 *
 * CSS: `desktop/system.css` `.dsk-tabs`, `.dsk-tab`, `.dsk-tab.is-active`.
 * Behaviour: ports BEHAVIOUR #5 from
 * `behaviours/desktop-behaviours.reference.js`.
 *
 * What #5 provides and this keeps:
 *   a real `[role="tablist"]` of `[role="tab"]` buttons  ·  click to switch  ·
 *   Left/Right/Home/End to move  ·  ROVING TABINDEX so the whole tablist is one
 *   tab stop, not one stop per tab  ·  each tab points at its panel through
 *   `aria-controls` and each panel points back through `aria-labelledby`.
 *
 * Arrow keys select as they move (`select(next, true)` in #5), which is the
 * automatic-activation pattern and is correct here because every panel is
 * already rendered client side; nothing is fetched on selection.
 *
 * The leave/enter animation classes #5 juggles are page-local, so the panel
 * swap here is a plain `hidden` toggle. `[hidden]` is guarded to
 * `display: none !important` in both `system.css` and `app-layer.css`, which is
 * the trap that made toggled grids look broken.
 *
 * Active tab: ink text plus the ACCENT underline, because an active tab is
 * state. The icon goes to fill weight for the same reason.
 */

const TabsContext = createContext(null);

export const Tabs = forwardRef(function Tabs(
  {
    tabs = [],
    value,
    defaultValue,
    onChange,
    label = 'Tabs',
    children,
    listClassName,
    className,
    ...rest
  },
  ref,
) {
  const base = useId();
  const listRef = useRef(null);
  /* Node map rather than a selector lookup: `useId` produces ids containing
     colons, which are not valid in a CSS selector without escaping. */
  const nodesRef = useRef(new Map());
  const [internal, setInternal] = useState(
    defaultValue != null ? defaultValue : tabs.length ? tabs[0].id : undefined,
  );

  const current = value !== undefined ? value : internal;

  const select = (id) => {
    if (id === current) return;
    if (value === undefined) setInternal(id);
    if (onChange) onChange(id);
  };

  const tabId = (id) => `${base}-tab-${id}`;
  const panelId = (id) => `${base}-panel-${id}`;

  const enabled = tabs.filter((tab) => !tab.disabled);

  const onKeyDown = (event) => {
    const active = document.activeElement;
    const at = enabled.findIndex((tab) => nodesRef.current.get(tab.id) === active);
    if (at < 0) return;

    let next = null;
    if (event.key === 'ArrowRight') next = enabled[(at + 1) % enabled.length];
    else if (event.key === 'ArrowLeft') next = enabled[(at - 1 + enabled.length) % enabled.length];
    else if (event.key === 'Home') next = enabled[0];
    else if (event.key === 'End') next = enabled[enabled.length - 1];
    if (!next) return;

    event.preventDefault();
    select(next.id);
    /* #5 moves focus with the selection, which is what makes the roving
       tabindex readable: the one tab stop is always the selected tab. */
    const node = nodesRef.current.get(next.id);
    if (node) node.focus();
  };

  return (
    <TabsContext.Provider value={{ current, tabId, panelId }}>
      <div ref={ref} className={className} {...rest}>
        <div
          ref={listRef}
          role="tablist"
          aria-label={label}
          className={cn('dsk-tabs', listClassName)}
          onKeyDown={onKeyDown}
        >
          {tabs.map((tab) => {
            const active = tab.id === current;
            return (
              <button
                key={tab.id}
                id={tabId(tab.id)}
                ref={(node) => {
                  if (node) nodesRef.current.set(tab.id, node);
                  else nodesRef.current.delete(tab.id);
                }}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls={panelId(tab.id)}
                /* Roving tabindex: exactly one tab is in the tab order. */
                tabIndex={active ? 0 : -1}
                disabled={tab.disabled}
                className={cn('dsk-tab', active && 'is-active')}
                onClick={() => select(tab.id)}
              >
                {tab.icon ? <Icon as={tab.icon} size="sm" active={active} /> : null}
                {tab.label}
              </button>
            );
          })}
        </div>
        {children}
      </div>
    </TabsContext.Provider>
  );
});

/**
 * TabPanel. Reads the current tab from context so a caller never has to wire
 * the id pair by hand.
 */
export const TabPanel = forwardRef(function TabPanel({ id, children, className, ...rest }, ref) {
  const context = useContext(TabsContext);
  if (!context) return null;

  const active = context.current === id;

  return (
    <div
      ref={ref}
      id={context.panelId(id)}
      role="tabpanel"
      aria-labelledby={context.tabId(id)}
      hidden={!active}
      tabIndex={active ? 0 : undefined}
      className={className}
      {...rest}
    >
      {active ? children : null}
    </div>
  );
});

export default Tabs;
